import * as THREE from 'three';

// ── READING THE STREETS OFF THE MODEL ────────────────────────────────────────
//
// The city GLB has no notion of "road": it is buildings and a ground mesh with
// an atlas painted on it. But from directly above, the atlas says it plainly —
// asphalt is dark, pavement is light, buildings cover both. So the city is
// photographed from overhead twice, once for the ground's own colours and once
// for what stands on it, and the street layout is read out of the pixels.
//
// That one map then serves everything that needs to know where the streets
// are: lamp placement along the kerbs, the radar's road layer, and anything
// that later wants to drive or walk them. It is derived, so it follows the
// model — a re-exported city, or a different zone, maps itself.
//
// Classes per texel: 0 nothing · 1 road · 2 pavement · 3 covered (building/prop)

export const ROAD = 1;
export const WALK = 2;
export const BLOCK = 3;

const PX_PER_UNIT = 4.3;   // ≈ 0.23 world units per texel (1024 across a 240-unit city)

// The height pass reads top-of-geometry height from an orthographic depth
// buffer, so its camera sits low with a tight range: 8 bits over ~61 units is
// a quarter unit per step, plenty to tell a kerb from a wall. Anything above the
// camera is clipped, which is fine — a tower's walls below it still cover.
const CAM_Y = 60; const NEAR = 1; const FAR = 62;

function renderTopDown(gl, root, rect, w, h, prepare) {
  const cam = new THREE.OrthographicCamera(-rect.w / 2, rect.w / 2, rect.h / 2, -rect.h / 2, NEAR, FAR);
  cam.position.set(rect.cx, CAM_Y, rect.cz);
  cam.up.set(0, 0, -1);
  cam.lookAt(rect.cx, 0, rect.cz);
  cam.updateMatrixWorld();
  const rt = new THREE.WebGLRenderTarget(w, h, { depthBuffer: true });
  const restore = prepare();
  const prevRT = gl.getRenderTarget();
  const prevClear = gl.getClearColor(new THREE.Color());
  const prevAlpha = gl.getClearAlpha();
  const prevAuto = gl.autoClear;
  const prevShadow = gl.shadowMap.autoUpdate;
  gl.shadowMap.autoUpdate = false;     // a map photo needs no shadows
  gl.autoClear = true;
  gl.setClearColor(0x000000, 0);
  gl.setRenderTarget(rt);
  gl.clear(true, true, true);
  gl.render(root, cam);
  const buf = new Uint8Array(w * h * 4);
  gl.readRenderTargetPixels(rt, 0, 0, w, h, buf);
  gl.setRenderTarget(prevRT);
  gl.setClearColor(prevClear, prevAlpha);
  gl.autoClear = prevAuto;
  gl.shadowMap.autoUpdate = prevShadow;
  restore();
  rt.dispose();
  return buf;
}

// Two-pass chamfer distance transform (1 / √2 weights) → distance in texels to the
// nearest texel where `isSrc` is true.
function distanceTo(cls, w, h, isSrc) {
  const INF = 1e9;
  const d = new Float32Array(w * h);
  for (let i = 0; i < w * h; i++) d[i] = isSrc(cls[i]) ? 0 : INF;
  const a = 1; const b = Math.SQRT2;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x; let v = d[i];
      if (x > 0) v = Math.min(v, d[i - 1] + a);
      if (y > 0) {
        v = Math.min(v, d[i - w] + a);
        if (x > 0) v = Math.min(v, d[i - w - 1] + b);
        if (x < w - 1) v = Math.min(v, d[i - w + 1] + b);
      }
      d[i] = v;
    }
  }
  for (let y = h - 1; y >= 0; y--) {
    for (let x = w - 1; x >= 0; x--) {
      const i = y * w + x; let v = d[i];
      if (x < w - 1) v = Math.min(v, d[i + 1] + a);
      if (y < h - 1) {
        v = Math.min(v, d[i + w] + a);
        if (x < w - 1) v = Math.min(v, d[i + w + 1] + b);
        if (x > 0) v = Math.min(v, d[i + w - 1] + b);
      }
      d[i] = v;
    }
  }
  return d;
}

/**
 * Photograph the city from above and classify every texel.
 * @returns {{ cls: Uint8Array, w: number, h: number, rect, toWorld, toTexel, dRoad: Float32Array, dBlock: Float32Array, stats }}
 */
export function buildRoadMap(gl, model, batches, bounds) {
  const minX = bounds.min[0], maxX = bounds.max[0], minZ = bounds.min[1], maxZ = bounds.max[1];
  const rect = { minX, minZ, w: maxX - minX, h: maxZ - minZ, cx: (minX + maxX) / 2, cz: (minZ + maxZ) / 2 };
  const W = Math.min(2048, Math.round(rect.w * PX_PER_UNIT));
  const H = Math.min(2048, Math.round(rect.h * PX_PER_UNIT));

  // Everything that draws in the model but is not a batch (the hero car, the
  // video signs) stays out of both photos.
  const others = [];
  model.traverse((o) => { if ((o.isMesh || o.isSprite) && !o.isBatchedMesh && o.visible) others.push(o); });

  const swap = (pick, matFor) => () => {
    const saved = batches.map((b) => [b, b.material, b.visible]);
    for (const b of batches) { b.visible = pick(b); if (b.visible) b.material = matFor(b); }
    others.forEach((o) => { o.userData.__rmVis = o.visible; o.visible = false; });
    return () => {
      for (const [b, m, v] of saved) { if (b.material !== m) { b.material.dispose(); b.material = m; } b.visible = v; }
      others.forEach((o) => { o.visible = o.userData.__rmVis; delete o.userData.__rmVis; });
    };
  };

  // 1 · Colour from above, everything (unlit). Where the top surface is ground,
  //     this is the ground's paint; where it is a roof, pass 2 says so.
  const all = () => true;
  const ground = renderTopDown(gl, model, rect, W, H, swap(all, (b) => new THREE.MeshBasicMaterial({ map: b.material.map || null, color: b.material.map ? 0xffffff : b.material.color, side: THREE.DoubleSide })));
  // 2 · HEIGHT of the top surface, from an orthographic depth pass. Not "is any
  //     non-road mesh here": some streets are paved with other materials' slabs
  //     (downtown's are), and a first version that treated every other mesh as
  //     a building left all of downtown unlit.
  const depth = renderTopDown(gl, model, rect, W, H, swap(all, () => new THREE.MeshDepthMaterial({ depthPacking: THREE.BasicDepthPacking, side: THREE.DoubleSide })));
  const heightAt = (si) => (depth[si + 3] === 0 ? -1 : CAM_Y - (NEAR + (1 - depth[si] / 255) * (FAR - NEAR)));

  // Rows come back bottom-up; the camera's screen-up is world −z, so row 0 is the
  // +z edge. Re-index so row r ↔ z = minZ + (r + 0.5) / H * rect.h.
  const UNDECIDED = 255;
  const cls = new Uint8Array(W * H);
  const lum = new Float32Array(W * H);
  const lums = [];
  for (let r = 0; r < H; r++) {
    const src = (H - 1 - r) * W;
    for (let x = 0; x < W; x++) {
      const si = (src + x) * 4; const di = r * W + x;
      if (ground[si + 3] === 0) { cls[di] = 0; continue; }
      if (heightAt(si) > 0.35) { cls[di] = BLOCK; continue; }
      const R = ground[si] / 255, G = ground[si + 1] / 255, B = ground[si + 2] / 255;
      // Painted centre lines are yellow and belong to the road whatever their
      // brightness.
      if (R > 0.25 && G > 0.18 && B < R * 0.55) { cls[di] = ROAD; continue; }
      lum[di] = 0.2126 * R + 0.7152 * G + 0.0722 * B;
      lums.push(lum[di]);
      cls[di] = UNDECIDED;
    }
  }

  // Split road from pavement at the valley of the ground's luminance histogram
  // (Otsu): the atlas's asphalt and paving sit in two clear humps.
  const t = otsu(lums);
  for (let i = 0; i < W * H; i++) if (cls[i] === UNDECIDED) cls[i] = lum[i] < t ? ROAD : WALK;

  // ROADS ARE WIDE. Dark tile grout, cracks and painted lines also fall below
  // the threshold, and treating them as road put a polka-dot grid of lamps
  // across every plaza. A road texel only counts if the dark region it belongs
  // to is at least ~1.6 units across: find the cores (texels ≥ 0.8 u from any
  // non-road), grow them back out by the same amount, and demote the rest.
  const u = rect.w / W;
  const inside = distanceTo(cls, W, H, (c) => c !== ROAD);
  const coreR = 0.8 / u;
  const fromCore = distanceTo(inside, W, H, (d) => d >= coreR);
  for (let i = 0; i < W * H; i++) {
    if (cls[i] === ROAD && fromCore[i] > coreR + 1) cls[i] = WALK;
  }

  const dRoad = distanceTo(cls, W, H, (c) => c === ROAD);
  const dBlock = distanceTo(cls, W, H, (c) => c === BLOCK || c === 0);
  // For road texels: how far to the kerb (0 off-road). Parking spots and
  // anything that wants "the lane next to the pavement" read this.
  const dEdge = distanceTo(cls, W, H, (c) => c !== ROAD);

  const sx = rect.w / W; const sz = rect.h / H;
  const toWorld = (x, r) => [minX + (x + 0.5) * sx, minZ + (r + 0.5) * sz];
  const toTexel = (wx, wz) => [Math.floor((wx - minX) / sx), Math.floor((wz - minZ) / sz)];
  let nRoad = 0, nWalk = 0, nBlock = 0;
  for (let i = 0; i < W * H; i++) { if (cls[i] === ROAD) nRoad++; else if (cls[i] === WALK) nWalk++; else if (cls[i] === BLOCK) nBlock++; }
  const stats = { W, H, threshold: +t.toFixed(3), road: +(nRoad / (W * H)).toFixed(3), walk: +(nWalk / (W * H)).toFixed(3), block: +(nBlock / (W * H)).toFixed(3) };
  const out = { cls, w: W, h: H, rect, toWorld, toTexel, dRoad, dBlock, dEdge, unitsPerTexel: sx, stats };
  if (typeof window !== 'undefined' && window.__look) {
    // Dev only: the raw passes, for inspecting what the classifier saw.
    out.debug = { ground, depth };
  }
  return out;
}

function otsu(values) {
  const bins = 64; const hist = new Float64Array(bins);
  for (const v of values) hist[Math.min(bins - 1, Math.floor(v * bins))] += 1;
  const total = values.length || 1;
  let sum = 0; for (let i = 0; i < bins; i++) sum += i * hist[i];
  let sumB = 0, wB = 0, best = 0, thr = 0.15 * bins;
  for (let i = 0; i < bins; i++) {
    wB += hist[i]; if (!wB) continue;
    const wF = total - wB; if (!wF) break;
    sumB += i * hist[i];
    const mB = sumB / wB, mF = (sum - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > best) { best = between; thr = i + 0.5; }
  }
  return thr / bins;
}
